import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Rocket, Building, TrendingUp, CheckCircle2, Target, Users, Briefcase } from 'lucide-react';

const WhoWeWorkWith = () => {
  const clientTypes = [
    {
      icon: TrendingUp,
      title: 'Investors (VC / Growth Capital / PE)',
      description: 'Funds seeking clarity on people risk before investment — and execution confidence after.',
      challenges: [
        'Clarity on people risk before investment',
        'Faster IC decisions',
        'Stronger post-deal value creation',
        'Contextual people insight for portfolio companies',
      ],
      howWeHelp: 'We support faster IC decisions and stronger post-deal value creation through contextual people insight.',
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/m2ca4dxz_Image%205%20-%20Who%20we%20work%20with%20-%20Investors%20-%201.jpg',
    },
    {
      icon: Rocket,
      title: 'Scaling Enterprises & Startups',
      description: 'High-growth organisations approaching Series B/C, IPO, or M&A.',
      challenges: [
        'Building investor-ready people foundations',
        'Reducing execution risk',
        'Supporting sustainable scale',
        'Scaling from early growth to institutional maturity',
      ],
      howWeHelp: 'We build investor-ready people foundations that reduce execution risk and support sustainable scale.',
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/dqps6392_Image%206%20-%20Who%20we%20work%20with%20-%20Scaling%20Enterprises%20%26%20Startups.jpg',
    },
    {
      icon: Building,
      title: 'Legacy Enterprises Taking the Next Leap',
      description: 'Promoter-led organisations preparing for generational transition or professionalisation.',
      challenges: [
        'Modernising structures and leadership depth',
        'Preserving the organisation\'s cultural soul',
        'Preparing for generational transition',
        'Professionalising founder-led businesses',
      ],
      howWeHelp: 'We help modernise structures and leadership depth while preserving the organisation\'s cultural soul.',
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/zr413xjf_Image%207%20-%20Who%20we%20work%20with%20-%20Legacy%20Enterprises.jpg',
    },
    {
      icon: Building,
      title: 'Large Organisations & MNCs',
      description: 'Established Indian companies and global organisations seeking focused, high-impact HR interventions.',
      challenges: [
        'Focused, high-impact HR interventions',
        'Specialist depth',
        'Speed and senior execution capability',
        'Structured transformation at scale',
      ],
      howWeHelp: 'We bring specialist depth, speed, and senior execution capability.',
      image: 'https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/49v2h1e9_Image%208%20-%20Who%20we%20work%20with%20-%20Large%20Organisation.jpg',
    },
  ];

  const fitCriteria = [
    'People strategy is embedded into business and investment priorities',
    'Decisions are guided by diagnostic insight and contextual understanding',
    'Leadership teams engage with data, perspective, and informed challenge',
    'Execution readiness is built deliberately, not assumed',
    'The organisation is navigating growth, transition, or increased scrutiny'
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=2070&q=80"
            alt="Business team meeting"
            className="w-full h-full object-cover opacity-15"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
              Who We Partner With
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                Focused on organisations that must deliver
              </span>
            </h1>
            <p className="text-xl lg:text-2xl max-w-3xl font-light mb-12 text-slate-300">
              Investors and organisations who see people capability as a driver of execution and growth.
            </p>
          </div>
        </div>
      </section>

      {/* Client Types Detail Sections */}
      {clientTypes.map((client, index) => {
        const Icon = client.icon;
        const isEven = index % 2 === 0;
        
        return (
          <section
            key={index}
            className={`py-20 lg:py-28 ${isEven ? 'bg-white' : 'bg-slate-100'}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className={`grid lg:grid-cols-2 gap-16 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}>
                {/* Content */}
                <div className={!isEven ? 'lg:col-start-2' : ''}>
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-amber-600" size={28} />
                  </div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4 leading-tight">
                    {client.title}
                  </h2>
                  <p className="text-xl text-slate-600 mb-8 leading-relaxed">{client.description}</p>

                  {/* Challenges */}
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Common challenges we solve</h3>
                    <ul className="space-y-3">
                      {client.challenges.map((challenge, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                          <p className="text-slate-600 text-base leading-relaxed">{challenge}</p>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* How We Help */}
                  <div className="p-6 bg-amber-50 border border-amber-200 rounded-xl">
                    <h3 className="text-lg font-bold text-slate-900 mb-3">How we help</h3>
                    <p className="text-slate-700 leading-relaxed">{client.howWeHelp}</p>
                  </div>
                </div>

                {/* Image */}
                <div className={!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative">
                    <img
                      src={client.image}
                      alt={client.title}
                      className="rounded-xl shadow-xl w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Who We Work Best With */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Who We Work Best With
              </h2>
              <p className="text-lg text-amber-600 font-medium mb-6">
                Partnerships focused on organisational readiness and execution strength
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We partner with investors, promoters, and leadership teams at moments where organisational capability becomes decisive to outcomes.
              </p>
              <p className="text-slate-700 font-medium mb-6">Our work typically supports contexts where:</p>
              <ul className="space-y-4">
                {fitCriteria.map((criterion, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-slate-600">{criterion}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/uuhkrgux_Image%209%20-%20Who%20we%20work%20with%20-%20Who%20we%20work%20best%20with.jpg"
                alt="Business partnership meeting"
                className="rounded-xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              If this resonates with where your organisation stands today,
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              let's examine whether a partnership makes strategic sense.
            </p>
            <Link
              to="/contact#ready-to-move"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-slate-900 font-semibold rounded-md hover:bg-amber-400 transition-all duration-200 shadow-lg group"
            >
              Let's Connect
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeWorkWith;
